/**
 * Simplified national flags for the currencies on the platform and the countries of its indices, drawn on a 24×24 box and shown
 * cropped to a circle. Detail is cut to what still reads at 14–40 px.
 */
import type { ReactNode } from 'react';

/** Five-pointed star centred on (x, y) with outer radius r. */
function Star({ x, y, r, fill }: { x: number; y: number; r: number; fill: string }) {
  const pts = Array.from({ length: 10 }, (_, k) => {
    const a = -Math.PI / 2 + (k * Math.PI) / 5;
    const d = k % 2 ? r * 0.4 : r;
    return `${(x + d * Math.cos(a)).toFixed(2)},${(y + d * Math.sin(a)).toFixed(2)}`;
  }).join(' ');
  return <polygon points={pts} fill={fill} />;
}

/** Union flag filling the 24×24 box (also scaled into the Australian and New Zealand cantons). */
const UNION = (
  <>
    <rect width="24" height="24" fill="#012169" />
    <path d="M0 0L24 24M24 0L0 24" stroke="#fff" strokeWidth="5" />
    <path d="M0 0L24 24M24 0L0 24" stroke="#c8102e" strokeWidth="1.8" />
    <path d="M12 0V24M0 12H24" stroke="#fff" strokeWidth="8" />
    <path d="M12 0V24M0 12H24" stroke="#c8102e" strokeWidth="4.8" />
  </>
);

const hBands = (colors: string[]) => colors.map((c, i) => <rect key={i} y={(24 / colors.length) * i} width="24" height={24 / colors.length + 0.1} fill={c} />);
const vBands = (colors: string[]) => colors.map((c, i) => <rect key={i} x={(24 / colors.length) * i} width={24 / colors.length + 0.1} height="24" fill={c} />);
const nordic = (bg: string, cross: string, inner?: string) => (
  <>
    <rect width="24" height="24" fill={bg} />
    <path d="M9 0V24M0 12H24" stroke={cross} strokeWidth={inner ? 5 : 3.6} />
    {inner && <path d="M9 0V24M0 12H24" stroke={inner} strokeWidth="2.4" />}
  </>
);
const southernCross = (fill: string, edge?: string) =>
  [
    [17.5, 18.5, 1.9],
    [14.5, 11.5, 1.6],
    [20, 9.5, 1.6],
    [17.5, 5, 1.6],
  ].map(([x, y, r], i) => (
    <g key={i}>
      {edge && <Star x={x!} y={y!} r={r! + 0.7} fill={edge} />}
      <Star x={x!} y={y!} r={r!} fill={fill} />
    </g>
  ));

export const FLAGS: Record<string, ReactNode> = {
  USD: (
    <>
      {hBands(Array.from({ length: 13 }, (_, i) => (i % 2 ? '#fff' : '#b22234')))}
      <rect width="11" height="13" fill="#3c3b6e" />
      {[2, 5, 8].flatMap((x) => [2.5, 6.5, 10.5].map((y) => <circle key={`${x}-${y}`} cx={x + 0.75} cy={y} r="0.8" fill="#fff" />))}
    </>
  ),
  EUR: (
    <>
      <rect width="24" height="24" fill="#003399" />
      {Array.from({ length: 12 }, (_, k) => {
        const a = (k * Math.PI) / 6;
        return <Star key={k} x={12 + 7 * Math.cos(a)} y={12 + 7 * Math.sin(a)} r={1.3} fill="#ffcc00" />;
      })}
    </>
  ),
  GBP: UNION,
  JPY: (
    <>
      <rect width="24" height="24" fill="#fff" />
      <circle cx="12" cy="12" r="6" fill="#bc002d" />
    </>
  ),
  CHF: (
    <>
      <rect width="24" height="24" fill="#d52b1e" />
      <path d="M12 5.5V18.5M5.5 12H18.5" stroke="#fff" strokeWidth="4" />
    </>
  ),
  CAD: (
    <>
      {vBands(['#d80621', '#fff', '#fff', '#d80621'])}
      <path d="M12 6l1.3 2.6 1.6-.6-.5 3.4 2.2-2 .5 1.4 2-.4-.8 2.4 1 .6-3.6 2.6.4 1.4-3.4-.5V18h-1.4v-1.9l-3.4.5.4-1.4-3.6-2.6 1-.6-.8-2.4 2 .4.5-1.4 2.2 2-.5-3.4 1.6.6z" fill="#d80621" />
    </>
  ),
  AUD: (
    <>
      <rect width="24" height="24" fill="#012169" />
      <g transform="scale(.5)">{UNION}</g>
      <Star x={6} y={18} r={2.6} fill="#fff" />
      {southernCross('#fff')}
    </>
  ),
  NZD: (
    <>
      <rect width="24" height="24" fill="#012169" />
      <g transform="scale(.5)">{UNION}</g>
      {southernCross('#c8102e', '#fff')}
    </>
  ),
  CNH: (
    <>
      <rect width="24" height="24" fill="#de2910" />
      <Star x={7} y={8} r={3.6} fill="#ffde00" />
      {[
        [12, 4],
        [14, 6.5],
        [14, 9.5],
        [12, 12],
      ].map(([x, y]) => (
        <Star key={`${x}-${y}`} x={x!} y={y!} r={1.1} fill="#ffde00" />
      ))}
    </>
  ),
  HKD: (
    <>
      <rect width="24" height="24" fill="#de2910" />
      {Array.from({ length: 5 }, (_, k) => (
        <ellipse key={k} cx="12" cy="8.3" rx="2" ry="3.6" fill="#fff" transform={`rotate(${k * 72} 12 12)`} />
      ))}
    </>
  ),
  SGD: (
    <>
      {hBands(['#ef3340', '#fff'])}
      <circle cx="7" cy="6" r="3.6" fill="#fff" />
      <circle cx="8.4" cy="6" r="3.4" fill="#ef3340" />
      {[
        [11, 3.4],
        [13.2, 5],
        [12.4, 7.6],
        [9.6, 7.6],
        [8.8, 5],
      ].map(([x, y]) => (
        <Star key={`${x}-${y}`} x={x! + 0.8} y={y!} r={0.9} fill="#fff" />
      ))}
    </>
  ),
  SEK: nordic('#006aa7', '#fecc00'),
  NOK: nordic('#ba0c2f', '#fff', '#00205b'),
  DKK: nordic('#c8102e', '#fff'),
  PLN: hBands(['#fff', '#dc143c']),
  HUF: hBands(['#ce2939', '#fff', '#477050']),
  CZK: (
    <>
      {hBands(['#fff', '#d7141a'])}
      <path d="M0 0L12 12L0 24z" fill="#11457e" />
    </>
  ),
  TRY: (
    <>
      <rect width="24" height="24" fill="#e30a17" />
      <circle cx="10" cy="12" r="5.6" fill="#fff" />
      <circle cx="11.4" cy="12" r="4.5" fill="#e30a17" />
      <Star x={16.2} y={12} r={2.4} fill="#fff" />
    </>
  ),
  MXN: (
    <>
      {vBands(['#006847', '#fff', '#ce1126'])}
      <circle cx="12" cy="12" r="2.2" fill="#8c5a2b" />
    </>
  ),
  ZAR: (
    <>
      {hBands(['#e03c31', '#001489'])}
      <path d="M0 0L12 12L0 24" fill="none" stroke="#fff" strokeWidth="8" />
      <path d="M12 12H24" stroke="#fff" strokeWidth="8" />
      <path d="M0 0L12 12L0 24" fill="none" stroke="#007749" strokeWidth="5" />
      <path d="M12 12H24" stroke="#007749" strokeWidth="5" />
      <path d="M0 3.5L8.5 12L0 20.5z" fill="#ffb612" />
      <path d="M0 5.5L6.5 12L0 18.5z" fill="#000" />
    </>
  ),
  // Countries whose currency is shared or not on the platform, for the flags of their indices.
  DE: <>{hBands(['#000', '#dd0000', '#ffce00'])}</>,
  FR: <>{vBands(['#002654', '#fff', '#ce1126'])}</>,
  NL: <>{hBands(['#ae1c28', '#fff', '#21468b'])}</>,
  ES: <>{hBands(['#aa151b', '#f1bf00', '#f1bf00', '#aa151b'])}</>,
  IN: (
    <>
      {hBands(['#ff9933', '#fff', '#138808'])}
      <circle cx="12" cy="12" r="2.6" fill="none" stroke="#000080" strokeWidth="0.9" />
    </>
  ),
  TW: (
    <>
      <rect width="24" height="24" fill="#fe0000" />
      <rect width="13" height="12" fill="#000095" />
      <circle cx="6.5" cy="6" r="2.6" fill="#fff" />
    </>
  ),
};

/** Round flag for a currency code (or one of the country codes above); a code without a flag gets its letters on a grey disc. */
export function Flag({ ccy, size, className }: { ccy: string; size: number; className?: string }) {
  const flag = FLAGS[ccy];
  return (
    <span className={className} style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', display: 'grid', placeItems: 'center', background: flag ? undefined : '#5b6270' }}>
      {flag ? (
        <svg viewBox="0 0 24 24" width={size} height={size} preserveAspectRatio="xMidYMid slice">
          {flag}
        </svg>
      ) : (
        <span style={{ fontSize: Math.round(size * 0.34), fontWeight: 700, color: '#fff' }}>{ccy.slice(0, 3)}</span>
      )}
    </span>
  );
}
